import React, { useCallback, useEffect, useState } from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';

import Waveform from 'js/src/components/app/waveform/Waveform';
import type { ImmutableWaveformData } from 'js/src/data/media/media-types';
import { noop } from 'js/src/libs/consts';

const sampleAudioData = (waveformData: Float32Array, numSamples: number) => {
    const bars = new Float32Array(numSamples);
    const samplesPerBar = waveformData.length / numSamples;

    for (let i = 0; i < numSamples; i++) {
        const start = Math.floor(i * samplesPerBar);
        const end = Math.floor((i + 1) * samplesPerBar);
        let totalVolume = 0;
        for (let j = start; j < end; j++) {
            const val = waveformData[j];
            totalVolume += val < 0 ? -val : val; // Use absolute value to get volume
        }
        bars[i] = totalVolume / (end - start);
    }
    return bars;
};

/**
 * Width of individual bar at max zoom level
 */
const BAR_MAX_WIDTH_PIXELS = 2;

export type Props = {
    source: string;
    onLoaded: VoidFunction;
    onAudioDetected: (hasAudio: boolean) => void;
    className?: string;
};

const TrimmerAudioTimeline = ({ source, onLoaded, onAudioDetected, className }: Props) => {
    const [waveformDataFetched, setWaveformDataFetched] = useState(false);
    const [originalWaveformData, setOriginalWaveformData] = useState<Float32Array>(
        new Float32Array(),
    );
    const [renderWaveformData, setRenderWaveformData] = React.useState<ImmutableWaveformData>(
        Immutable.List(),
    );

    const handleWaveformResize = useCallback(
        (width: number) => {
            if (width <= 0 || originalWaveformData.length === 0) return;
            const numBars = Math.max(1, Math.floor(width / BAR_MAX_WIDTH_PIXELS));
            const bars = sampleAudioData(originalWaveformData, numBars);
            setRenderWaveformData(Immutable.List(bars));
        },
        [originalWaveformData],
    );

    useEffect(() => {
        const abortController = new AbortController();
        const computeAndStoreWaveformData = async () => {
            try {
                const audioContext = new AudioContext();
                const response = await fetch(source, {
                    signal: abortController.signal,
                });
                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                const rawAudioData = audioBuffer.getChannelData(0); // We only need to work with one channel of data

                // long clips (1hr) can have over 100M data points. We don't need that much resolution
                // to draw the waveform, and keeping it all causes performance issues. So downsample it to
                // max 10k data points.
                const downSampledAudioData = sampleAudioData(
                    rawAudioData,
                    Math.min(10000, rawAudioData.length),
                );
                setOriginalWaveformData(downSampledAudioData);
            } catch (e) {
                /**
                 * Possible reasons for error:
                 * - AudioContext is not supported
                 * - Fetch request was aborted
                 * - Fetch request failed
                 * - Decoding audio data failed
                 *
                 * In all these cases, we just want to ignore the error and not do anything.
                 * The waveform will not be displayed in the UI.
                 **/
            } finally {
                setWaveformDataFetched(true);
            }
        };
        computeAndStoreWaveformData().catch(noop);
        return () => {
            /**
             * Abort any pending fetch requests when
             * the component unmounts or re-renders
             */
            abortController.abort();
        };
    }, [source]);

    useEffect(() => {
        if (waveformDataFetched && onLoaded) {
            const hasAudio = originalWaveformData.length > 0;
            onAudioDetected(hasAudio);
            onLoaded();
        }
    }, [onAudioDetected, onLoaded, originalWaveformData, waveformDataFetched]);

    return (
        <div className={classNames('trimmer-audio-timeline', className)}>
            <Waveform
                waveformData={renderWaveformData}
                fillPercentage={0}
                onResize={handleWaveformResize}
            />
        </div>
    );
};

export default TrimmerAudioTimeline;
